import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Archive, ExternalLink, Plus, Star, StarOff } from "lucide-react";

import {
  WorkspacesService,
  type Workspace,
  type WorkspaceListResponse,
  type WorkspaceStatus,
} from "@/api";
import { useApi, useMutation } from "@/hooks/use-api";
import { useActiveWorkspace } from "@/hooks/use-active-workspace";
import { useAuth } from "@/hooks/use-auth";
import { useTranslation } from "@/i18n";
import type { TranslationKey } from "@/i18n";
import { parseApiError } from "@/lib/api-error";
import { ROUTES } from "@/lib/constants";
import { formatDateID } from "@/lib/format";
import {
  PILOT_MAX_WORKSPACES_PER_TENANT,
  WORKSPACE_STATUS_TONE,
} from "@/lib/workspace-helpers";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PageHeader } from "@/components/shared/page-header";
import { Section } from "@/components/shared/section";
import { AddWorkspaceModal } from "@/components/workspaces/add-workspace-modal";
import { ArchiveWorkspaceDialog } from "@/components/workspaces/archive-workspace-dialog";
import { MoreVertical } from "lucide-react";

const TENANT_ADMIN_ROLES = new Set(["admin", "super_admin"]);

// Workspace lifecycle states map to translation keys so the
// status badge re-renders on a locale switch.
const WORKSPACE_STATUS_KEY: Record<WorkspaceStatus, TranslationKey> = {
  active: "workspaces.status.active",
  archived: "workspaces.status.archived",
};

export default function WorkspacesPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const isTenantAdmin = user ? TENANT_ADMIN_ROLES.has(user.role) : false;
  const { refreshWorkspaces } = useActiveWorkspace();

  // Backend ships ``status`` as a plain string; only translate the
  // canonical lifecycle states and fall back to the raw value.
  const resolveStatusLabel = (status: string): string => {
    const key = WORKSPACE_STATUS_KEY[status as WorkspaceStatus];
    return key ? t(key) : status;
  };

  const [addOpen, setAddOpen] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState<Workspace | null>(null);

  const fetcher = useCallback(
    () =>
      WorkspacesService.listPaged({
        include_archived: true,
        limit: 200,
        offset: 0,
      }),
    []
  );
  const { data, error, isLoading, refetch } =
    useApi<WorkspaceListResponse>(fetcher);

  const items = useMemo(() => data?.items ?? [], [data]);
  const activeCount = items.filter((w) => w.status === "active").length;
  const remaining = Math.max(0, PILOT_MAX_WORKSPACES_PER_TENANT - activeCount);

  const setDefault = useMutation<string, Workspace>(
    (id) => WorkspacesService.setDefault(id),
    {
      onSuccess: () => {
        void refetch();
        void refreshWorkspaces();
      },
    }
  );

  const handleCreated = async () => {
    setAddOpen(false);
    await refetch();
    await refreshWorkspaces();
  };

  const handleArchived = async () => {
    setArchiveTarget(null);
    await refetch();
    await refreshWorkspaces();
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("workspaces.page.title")}
        description={t("workspaces.page.description", {
          active: activeCount,
          max: PILOT_MAX_WORKSPACES_PER_TENANT,
        })}
        action={
          isTenantAdmin ? (
            <Button
              onClick={() => setAddOpen(true)}
              disabled={remaining === 0}
              size="sm"
            >
              <Plus size={14} />
              {t("workspaces.actions.create")}
            </Button>
          ) : undefined
        }
      />

      {error && <Alert variant="danger">{parseApiError(error)}</Alert>}
      {setDefault.error && (
        <Alert variant="danger">{parseApiError(setDefault.error)}</Alert>
      )}
      {!isTenantAdmin && (
        <Alert variant="info">{t("workspaces.page.readOnlyNotice")}</Alert>
      )}
      {remaining === 0 && isTenantAdmin && (
        <Alert variant="warning">
          {t("workspaces.page.limitReached", {
            max: PILOT_MAX_WORKSPACES_PER_TENANT,
          })}
        </Alert>
      )}

      <Section
        title={t("workspaces.page.sectionTitle")}
        description={t("workspaces.page.sectionDescription")}
      >
        <div className="overflow-hidden rounded-lg border border-ocean-100 bg-white">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("workspaces.table.name")}</TableHead>
                <TableHead>{t("workspaces.table.slug")}</TableHead>
                <TableHead>{t("workspaces.table.status")}</TableHead>
                <TableHead>{t("workspaces.table.default")}</TableHead>
                <TableHead>{t("workspaces.table.members")}</TableHead>
                <TableHead className="hidden tablet:table-cell">
                  {t("workspaces.table.created")}
                </TableHead>
                <TableHead className="text-right">
                  {t("workspaces.table.actions")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && items.length === 0 && (
                <>
                  {[0, 1, 2].map((i) => (
                    <TableRow key={`ws-sk-${i}`}>
                      <TableCell colSpan={7} className="py-4">
                        <Skeleton className="h-5 w-full" />
                      </TableCell>
                    </TableRow>
                  ))}
                </>
              )}
              {!isLoading && items.length === 0 && (
                <TableEmpty
                  colSpan={7}
                  message={t("workspaces.table.empty")}
                />
              )}
              {items.map((ws) => (
                <TableRow
                  key={ws.id}
                  className="cursor-pointer"
                  onClick={() => navigate(`${ROUTES.WORKSPACES}/${ws.id}`)}
                >
                  <TableCell className="font-medium text-ocean-900">
                    {ws.name}
                  </TableCell>
                  <TableCell>
                    <code className="text-xs text-ocean-700">{ws.slug}</code>
                  </TableCell>
                  <TableCell>
                    <Badge tone={WORKSPACE_STATUS_TONE[ws.status] ?? "neutral"}>
                      {resolveStatusLabel(ws.status)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {ws.is_default ? (
                      <Badge tone="ocean">
                        {t("workspaces.badges.isDefault")}
                      </Badge>
                    ) : (
                      <span className="text-xs text-neutral-400">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-neutral-700">
                    {ws.member_count}
                  </TableCell>
                  <TableCell className="hidden tablet:table-cell text-xs text-neutral-600">
                    {formatDateID(ws.created_at)}
                  </TableCell>
                  <TableCell
                    className="text-right"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          aria-label={t("workspaces.actions.rowAriaLabel", {
                            name: ws.name,
                          })}
                        >
                          <MoreVertical size={14} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="right">
                        <DropdownMenuItem
                          onClick={() =>
                            navigate(`${ROUTES.WORKSPACES}/${ws.id}`)
                          }
                        >
                          <ExternalLink size={14} />
                          {t("workspaces.actions.openDetail")}
                        </DropdownMenuItem>
                        {isTenantAdmin &&
                          ws.status === "active" &&
                          !ws.is_default && (
                            <DropdownMenuItem
                              onClick={() => void setDefault.mutate(ws.id)}
                              disabled={setDefault.isLoading}
                            >
                              <Star size={14} />
                              {t("workspaces.actions.setDefault")}
                            </DropdownMenuItem>
                          )}
                        {isTenantAdmin && ws.is_default && (
                          <DropdownMenuItem disabled>
                            <StarOff size={14} />
                            {t("workspaces.actions.defaultActive")}
                          </DropdownMenuItem>
                        )}
                        {isTenantAdmin && ws.status === "active" && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => setArchiveTarget(ws)}
                              variant="danger"
                            >
                              <Archive size={14} />
                              {t("workspaces.actions.archive")}
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Section>

      <AddWorkspaceModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={handleCreated}
      />

      <ArchiveWorkspaceDialog
        workspace={archiveTarget}
        allWorkspaces={items}
        onClose={() => setArchiveTarget(null)}
        onArchived={handleArchived}
      />
    </div>
  );
}
